/**
 * The webinar funnel as the host's Meta pixel hears it, from the watch page
 * and from our server. Both sides build every event from here, so a moment the
 * browser reports and the same moment the server reports carry one name and
 * one id, and Meta counts it once (it de-duplicates on the pair for 48 hours).
 *
 * The moments are the ones the app already records, skips included: a person
 * who drags to the offer and presses it is a real signal.
 */
import type { OfferKind } from "./types";

/**
 * What every event says it is about: the webinar by its slug, and by its id,
 * which never changes even when the slug does. One pixel serves all of a
 * workspace's webinars, and hosts tell them apart in Meta by these.
 */
export interface MetaContent {
  content_name: string;
  content_ids: string[];
}

export function metaContent(slug: string, webinarId: string): MetaContent {
  return { content_name: slug, content_ids: [webinarId] };
}

/** Each moment a person passes, in funnel order. */
export type MetaStep = "lead" | "view" | "progress" | "offer" | "press" | "done" | "customer";

/**
 * Meta's name for each moment. Standard events wherever one fits: if Meta ever
 * holds a pixel's custom events for review, the standard ones keep flowing.
 */
const STEP_EVENT: Record<Exclude<MetaStep, "press" | "customer">, string> = {
  lead: "Lead",
  view: "ViewContent",
  progress: "WatchProgress",
  offer: "ReachedOffer",
  done: "WebinarCompleted",
};

const STANDARD = new Set(["Lead", "ViewContent", "InitiateCheckout", "Schedule", "Purchase"]);

/** True for one of Meta's own event names; the pixel sends the rest with `trackCustom`. */
export function isStandardMetaEvent(name: string): boolean {
  return STANDARD.has(name);
}

/** The press on the offer, named for what the offer is. */
export function pressEventName(kind: OfferKind): string {
  if (kind === "buy") return "InitiateCheckout";
  if (kind === "call") return "Schedule";
  return "ClickedOffer";
}

/** Meta's name for a moment; a press is named by its offer, a customer by whether a value came with it. */
export function metaEventName(step: MetaStep, detail?: { offerKind?: OfferKind; withValue?: boolean }): string {
  if (step === "press") return pressEventName(detail?.offerKind ?? "call");
  if (step === "customer") return detail?.withValue ? "Purchase" : "BecameCustomer";
  return STEP_EVENT[step];
}

/** How far into the talk each progress event is sent, in percent. */
const PROGRESS_MARKS = [25, 50, 75] as const;

/**
 * The marks one move of the playhead passed, the way the app decides it has
 * reached the offer: from before a mark to on or after it.
 */
export function marksCrossed(priorPct: number, nextPct: number): number[] {
  return PROGRESS_MARKS.filter((m) => priorPct < m && nextPct >= m);
}

/**
 * The event id. A step that happens more than once for a person carries what
 * tells the times apart: the percent, or the live session.
 */
export function metaEventId(step: MetaStep, key: string, extra?: string | number): string {
  return extra === undefined || extra === "" ? `${step}_${key}` : `${step}_${key}_${extra}`;
}

/** SHA-256 of some text, as lowercase hex. */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * The per-person key in every event id. Why not the watch token: the token opens
 * the person's room, and nothing sent to Meta may carry it.
 */
export async function metaKey(registrantId: string): Promise<string> {
  return (await sha256Hex(registrantId)).slice(0, 32);
}
